import {isSnapshotRecord} from '@PosterWhiteboard/collaboration/record-changes.library';
import type {SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';

const PAGES_KEY = 'pages';
const PAGES_HASH_MAP_KEY = 'pagesHashMap';
const PAGE_ORDER_KEY = 'pageOrder';
const ITEMS_KEY = 'items';
const ITEMS_HASH_MAP_KEY = 'itemsHashMap';
const ITEMS_ORDER_KEY = 'itemsOrder';
const GROUPS_KEY = 'groups';
const GROUPS_HASH_MAP_KEY = 'groupsHashMap';

const getNestedRecord = (record: SnapshotRecord, key: string): SnapshotRecord => {
  const value = record[key];
  return isSnapshotRecord(value) ? value : {};
};

const getIdList = (record: SnapshotRecord, key: string): string[] => {
  const value: unknown = record[key];
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
};

export const getPagesById = (snapshot: SnapshotRecord): SnapshotRecord => {
  return getNestedRecord(getNestedRecord(snapshot, PAGES_KEY), PAGES_HASH_MAP_KEY);
};

export const getPageOrder = (snapshot: SnapshotRecord): string[] => {
  return getIdList(getNestedRecord(snapshot, PAGES_KEY), PAGE_ORDER_KEY);
};

export const getPage = (snapshot: SnapshotRecord, pageId: string): SnapshotRecord | null => {
  const page = getPagesById(snapshot)[pageId];
  return isSnapshotRecord(page) ? page : null;
};

export const getPosterProps = (snapshot: SnapshotRecord): SnapshotRecord => {
  const {[PAGES_KEY]: _pages, ...posterProps} = snapshot;
  return posterProps;
};

export const replacePosterProps = (snapshot: SnapshotRecord, posterProps: SnapshotRecord): SnapshotRecord => {
  return {...posterProps, [PAGES_KEY]: snapshot[PAGES_KEY]};
};

export const replacePages = (snapshot: SnapshotRecord, pagesById: SnapshotRecord, pageOrder: string[]): SnapshotRecord => {
  return {...snapshot, [PAGES_KEY]: {...getNestedRecord(snapshot, PAGES_KEY), [PAGES_HASH_MAP_KEY]: pagesById, [PAGE_ORDER_KEY]: pageOrder}};
};

export const getItemsById = (page: SnapshotRecord): SnapshotRecord => {
  return getNestedRecord(getNestedRecord(page, ITEMS_KEY), ITEMS_HASH_MAP_KEY);
};

export const getItemsOrder = (page: SnapshotRecord): string[] => {
  return getIdList(getNestedRecord(page, ITEMS_KEY), ITEMS_ORDER_KEY);
};

export const getItem = (page: SnapshotRecord, itemId: string): SnapshotRecord | null => {
  const item = getItemsById(page)[itemId];
  return isSnapshotRecord(item) ? item : null;
};

export const replaceItems = (page: SnapshotRecord, itemsById: SnapshotRecord, itemsOrder: string[]): SnapshotRecord => {
  return {...page, [ITEMS_KEY]: {...getNestedRecord(page, ITEMS_KEY), [ITEMS_HASH_MAP_KEY]: itemsById, [ITEMS_ORDER_KEY]: itemsOrder}};
};

export const getGroupsById = (page: SnapshotRecord): SnapshotRecord => {
  return getNestedRecord(getNestedRecord(getNestedRecord(page, ITEMS_KEY), GROUPS_KEY), GROUPS_HASH_MAP_KEY);
};

export const replaceGroups = (page: SnapshotRecord, groupsById: SnapshotRecord): SnapshotRecord => {
  const items = getNestedRecord(page, ITEMS_KEY);
  return {...page, [ITEMS_KEY]: {...items, [GROUPS_KEY]: {...getNestedRecord(items, GROUPS_KEY), [GROUPS_HASH_MAP_KEY]: groupsById}}};
};

/** Everything on a page except its item records and their order, which have dedicated ops. */
export const getPageProps = (page: SnapshotRecord): SnapshotRecord => {
  const {[ITEMS_HASH_MAP_KEY]: _itemsById, [ITEMS_ORDER_KEY]: _itemsOrder, ...itemsProps} = getNestedRecord(page, ITEMS_KEY);
  return {...page, [ITEMS_KEY]: itemsProps};
};

export const replacePageProps = (page: SnapshotRecord, pageProps: SnapshotRecord): SnapshotRecord => {
  return replaceItems({...pageProps, [ITEMS_KEY]: getNestedRecord(pageProps, ITEMS_KEY)}, getItemsById(page), getItemsOrder(page));
};
